import type { GetStaticPaths, NextPage } from 'next'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { fetchApi } from '@hozana/api/fetchApi'
import { modifyQueryEntities } from '@hozana/api/functions'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { TTitleGetter } from '@hozana/api/server/common'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { useAction } from '@hozana/hooks/useAction'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFullUrl, getQueryValues, translateUrl } from '@hozana/router/functions'
import { IMAGE_SIZES, getImageUrl } from '@hozana/styling/image'
import { TRACKING_SOURCE, UTM } from '@hozana/tracking/constants'

import { Div } from 'elements/layout/Div'

import { addFavorite } from 'config/routines'
import { openPopin } from 'general/actions'
import { QUERY_ACTION } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { COMMUNITY_PAGE_TABS } from 'modules/community/constants'
import { SpecialPublicationHeader } from 'modules/publication/components/SpecialPublicationHeader'
import { isPublicationPublished } from 'modules/publication/functions'
import { getPublicationQuery, incrementPublicationViewsCountMutation } from 'modules/publication/queries'
import { selectPublication, selectPublicationCommunity } from 'modules/publication/selectors'
import type { TPublication } from 'modules/publication/types'
import { SHARE_OBJECTS } from 'modules/share/constants'

import { PreloadPublicationPage } from './PreloadPublicationPage'
import { PublicationPageContent } from './PublicationPageContent'

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

const getPublicationPageApiQueries = (publicationId: number) => ({
  publication: {
    query: modifyQueryEntities(getPublicationQuery({ publicationId }), {
      community: (community) => ({ ...community, description: undefined }),
    }),
    displayErrors: true,
  },
})

const getRedirect = (publication: TPublication) => ({
  redirect: {
    permanent: true,
    destination: translateUrl(
      {
        pathname: PAGE.PUBLICATION_VIEW,
        query: {
          publicationId: publication.id,
          publicationSlug: publication.slug,
        },
        host: window.location.host,
      },
      publication.lang,
      {
        format: 'string',
      },
    ),
  },
})

const publicationNameTitleGetter: TTitleGetter = ({ store, params }) =>
  selectPublication(store.getState(), Number(params.publicationId))?.name

export const getStaticProps = createGetStaticProps(
  PAGE.PUBLICATION_VIEW,
  publicationNameTitleGetter,
  async (ctx) => {
    const publicationId = Number(ctx.params.publicationId)

    const data = await createGetStaticData(() => getPublicationPageApiQueries(publicationId))(ctx)

    // Redirect to the right language if current locale is not publication.lang
    if ('props' in data) {
      const publication = selectPublication(ctx.store.getState(), publicationId)
      if (publication && publication.lang !== ctx.locale) {
        return getRedirect(publication)
      }
    }
    return data
  },
  { namespaces: [NAMESPACE.PUBLICATION, NAMESPACE.COMMUNITY, NAMESPACE.PRAY] },
)

export const PublicationPage: NextPage = () => {
  const { query, replace, push } = useRouter() // #router.replace
  const dispatch = useDispatch()
  const {
    i18n: { language },
  } = useTranslation()
  const publicationId = Number(query.publicationId)
  const community = useSelector((state) => selectPublicationCommunity(state, Number(query.publicationId)))

  useEffect(() => {
    if (publicationId) {
      dispatch(fetchApi(incrementPublicationViewsCountMutation({ publicationId })))
    }
  }, [dispatch, publicationId])

  const [, publicationQuery] = useApiData(
    () => ({
      query: getPublicationQuery({ publicationId }),
      disabled: !publicationId,
    }),
    [publicationId],
  )

  const publication = useSelector((state) => selectPublication(state, publicationId))

  useEffect(() => {
    // Ensure url is correctUrl
    if (publication?.slug && query.publicationSlug !== publication.slug) {
      replace(
        isPublicationPublished(publication)
          ? {
              pathname: PAGE.PUBLICATION_VIEW,
              query: {
                publicationId: publication.id,
                publicationSlug: publication.slug,
                ...getQueryValues(window.location),
              },
              host: window.location.host,
            }
          : {
              pathname: PAGE.COMMUNITY_VIEW_ADMIN,
              query: {
                communityId: publication.communityId,
                tab: COMMUNITY_PAGE_TABS.SCHEDULED_AND_DRAFT,
              },
            },
      )
    }
  }, [publication, query.publicationSlug, replace])

  useAction(
    () =>
      dispatch(
        openPopin(POPINS.InvitePopin, {
          objectToShare: SHARE_OBJECTS.PUBLICATION,
          utmContent: UTM.CONTENT.PUBLICATION_PAGE_URL_INVITE_POPIN,
          trackingSource: TRACKING_SOURCE.EMAIL_LINK,
          publication,
          community,
        }),
      ),
    [community, dispatch, publication],
    QUERY_ACTION.INVITE,
    { disabled: !publication },
  )

  useAction(
    () => {
      dispatch(addFavorite(publication))
    },
    [dispatch, publication],
    QUERY_ACTION.ADD_FAVORITE,
    { disabled: !publication },
  )

  useAction(
    () => {
      window.location.href = translateUrl(
        {
          pathname: PAGE.PUBLICATION_VIEW,
          query: {
            publicationId: publication?.id,
            publicationSlug: publication?.slug,
            utm_medium: UTM.MEDIUM.MAILTO,
            utm_campaign: UTM.CAMPAIGN.COMMUNITY(community?.id, community?.slug),
            utm_content: UTM.CONTENT.PUBLICATION_MAIL_MAILTO_SHARE_BUTTON,
          },
        },
        language,
        { format: 'string' },
      )
    },
    [language, community?.id, community?.slug, publication?.id, publication?.slug],
    QUERY_ACTION.SHARE_MAIL_PUBLI,
    { disabled: !publication },
  )

  // If no publication slug is not defined, wait for page reload before rendering
  if (!query.publicationSlug) return null

  const metaDescriptionMaxLength = 160
  let metaDescription = publication?.content?.replace(/\r?\n|\r/g, '').replace(/<[^<>]+>/g, '')
  if (metaDescription?.length > metaDescriptionMaxLength) {
    metaDescription = metaDescription.substring(0, metaDescriptionMaxLength - '...'.length).concat('...')
  }

  const publicationImageWidth = IMAGE_SIZES.publication.big_no_height_limit
  const communityImageWidth = IMAGE_SIZES.community.medium

  return (
    <Page>
      <ApiHandler queries={[publicationQuery]} display="fullPage" onRetry={publicationQuery.refetch} noLoadingDisplay />
      {!publication && publicationQuery.loading && <PreloadPublicationPage />}
      {publication && (
        <main>
          <Head
            type="article"
            article={{
              published_time: new Date(publication.publishedDate).toISOString(),
              modified_time: new Date(publication.updatedAt).toISOString(),
              author: community && `/community/${community.id}?action=show-user&userid=${community.user}`,
            }}
            title={publication.name}
            language={publication.lang}
            description={metaDescription}
            isIndexed={publication.isSeoIndexed !== false && publication.lang === language}
            image={
              publication.picture
                ? getImageUrl(publication.picture, publicationImageWidth)
                : getImageUrl(community?.picture, communityImageWidth)
            }
            imageWidth={publication.picture && publicationImageWidth}
            links={{
              canonical: getFullUrl(
                {
                  pathname: PAGE.PUBLICATION_VIEW,
                  query: publication?.parentPublication
                    ? {
                        publicationId: publication.parentPublication?.id,
                        publicationSlug: publication.parentPublication?.slug,
                      }
                    : {
                        publicationId: publication.id,
                        publicationSlug: publication.slug,
                      },
                },
                language,
              ),
            }}
          />
          {!publication.published && (
            <Div p="5px 0 0 0">
              <SpecialPublicationHeader
                publication={publication}
                onClickEdit={() => push({ pathname: PAGE.PUBLICATION_EDIT, query })}
              />
            </Div>
          )}
          <PublicationPageContent publication={publication} community={community} />
        </main>
      )}
    </Page>
  )
}
